import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { apiRequest } from '../utils/apiClient';

// A visitor's saved "daily place" (work, kids' school…). Names only — the
// driving estimate is computed client-side per property.
export interface TravelPlace {
  id: string;
  name: string;
}

// Logged-out visitors persist here (kept from the original TravelTimeCard so
// existing anonymous places survive this change). Signed-in users also get a
// per-account cache for instant paint before the server responds.
const ANON_KEY = 'safaqat24.travelPlaces.v1';
const userKey = (userId: string) => `safaqat24.travelPlaces.${userId}`;

function readPlaces(key: string): TravelPlace[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as TravelPlace[];
    return Array.isArray(parsed)
      ? parsed.filter((p) => p && typeof p.name === 'string' && p.name.trim())
      : [];
  } catch {
    return [];
  }
}

function writePlaces(key: string, places: TravelPlace[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(places));
  } catch {
    // localStorage unavailable — places just won't persist
  }
}

const norm = (name: string) => name.trim().toLowerCase();

interface TravelPlacesApi {
  places: TravelPlace[];
  loading: boolean;
  /** Resolves `true` when a new place was added, `false` for an empty or duplicate name. */
  addPlace: (name: string) => Promise<boolean>;
  removePlace: (id: string) => Promise<void>;
}

/**
 * Auth-aware persistence for the property-page "daily places".
 * - Logged out → browser storage only.
 * - Logged in  → synced to the account (GET/POST/DELETE /travel-places), with a
 *   per-user localStorage cache. Places added while logged out are merged into
 *   the account once on sign-in, then cleared from anonymous storage.
 * Every server call degrades gracefully to local storage on failure, so the
 * feature keeps working if the API is unavailable.
 */
export function useTravelPlaces(): TravelPlacesApi {
  const { user, session } = useAuth();
  const userId = user?.id ?? null;
  const token = session?.token ?? null;

  const [places, setPlaces] = useState<TravelPlace[]>([]);
  const [loading, setLoading] = useState(false);
  // Guards the one-time anonymous→account migration per signed-in user.
  const migratedForRef = useRef<string | null>(null);
  // Synchronous mirror of `places` for dedupe checks inside callbacks.
  const placesRef = useRef<TravelPlace[]>([]);
  useEffect(() => { placesRef.current = places; }, [places]);

  // Load (and for signed-in users, sync) whenever auth state changes.
  useEffect(() => {
    let cancelled = false;

    // Logged out: browser storage is the source of truth.
    if (!userId || !token) {
      setPlaces(readPlaces(ANON_KEY));
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    // Instant paint from the per-user cache while the server responds.
    setPlaces(readPlaces(userKey(userId)));

    (async () => {
      try {
        // One-time merge of anonymous places into the account.
        if (migratedForRef.current !== userId) {
          migratedForRef.current = userId;
          const anon = readPlaces(ANON_KEY);
          if (anon.length > 0) {
            await Promise.all(
              anon.map((p) =>
                apiRequest('/travel-places', { method: 'POST', body: { name: p.name }, token }).catch(() => null),
              ),
            );
            writePlaces(ANON_KEY, []);
          }
        }

        const res = await apiRequest<{ places: TravelPlace[] }>('/travel-places', { token });
        if (cancelled) return;
        const server = (res.places ?? []).map((p) => ({ id: p.id, name: p.name }));
        setPlaces(server);
        writePlaces(userKey(userId), server);
      } catch {
        // API unavailable — keep the per-user cache already shown.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userId, token]);

  const addPlace = useCallback(async (rawName: string): Promise<boolean> => {
    const name = rawName.trim();
    if (!name) return false;

    // De-dupe by name (case-insensitive) against what's already shown.
    if (placesRef.current.some((p) => norm(p.name) === norm(name))) return false;

    const temp: TravelPlace = { id: `temp_${Date.now()}`, name };

    if (userId && token) {
      setPlaces((prev) => [...prev, temp]);
      // Sync to the account in the background so the caller (and any "saved"
      // confirmation it shows) isn't blocked on the network. On success swap
      // the temp id for the server id; on failure keep the optimistic entry.
      void (async () => {
        try {
          const res = await apiRequest<{ place: TravelPlace }>('/travel-places', {
            method: 'POST',
            body: { name },
            token,
          });
          setPlaces((prev) => {
            const next = prev.map((p) => (p.id === temp.id ? { id: res.place.id, name: res.place.name } : p));
            writePlaces(userKey(userId), next);
            return next;
          });
        } catch {
          // Sync failed — keep the optimistic entry in the per-user cache.
          setPlaces((prev) => {
            writePlaces(userKey(userId), prev);
            return prev;
          });
        }
      })();
      return true;
    }

    // Logged out: persist to anonymous storage.
    setPlaces((prev) => {
      const next = [...prev, temp];
      writePlaces(ANON_KEY, next);
      return next;
    });
    return true;
  }, [userId, token]);

  const removePlace = useCallback(async (id: string) => {
    let removed: TravelPlace | undefined;
    setPlaces((prev) => {
      removed = prev.find((p) => p.id === id);
      const next = prev.filter((p) => p.id !== id);
      writePlaces(userId && token ? userKey(userId) : ANON_KEY, next);
      return next;
    });

    if (userId && token && removed && !id.startsWith('temp_')) {
      try {
        await apiRequest(`/travel-places/${id}`, { method: 'DELETE', token });
      } catch {
        // Best-effort: local state already updated; server will reconcile on next load.
      }
    }
  }, [userId, token]);

  return { places, loading, addPlace, removePlace };
}
